import { useState, type ComponentPropsWithoutRef, type JSX, type ReactNode } from 'react';

import { cx } from '../internal/cx.js';

export interface ListProps extends ComponentPropsWithoutRef<'ul'> {
  children: ReactNode;
}

/** Vertical list container for navigation and grouped content. */
export function List({ children, className, ...props }: ListProps): JSX.Element {
  return (
    <ul className={cx('nrx-list', className)} {...props}>
      {children}
    </ul>
  );
}

export interface ListItemProps extends ComponentPropsWithoutRef<'li'> {
  children: ReactNode;
  disableGutters?: boolean;
}

/** List row wrapper. */
export function ListItem({
  children,
  className,
  disableGutters = false,
  ...props
}: ListItemProps): JSX.Element {
  return (
    <li
      className={cx('nrx-list__item', disableGutters && 'nrx-list__item--no-gutters', className)}
      {...props}
    >
      {children}
    </li>
  );
}

export interface ListItemIconProps extends ComponentPropsWithoutRef<'span'> {
  children: ReactNode;
}

/** Leading icon slot inside a list item button. */
export function ListItemIcon({ children, className, ...props }: ListItemIconProps): JSX.Element {
  return (
    <span className={cx('nrx-list-item__icon', className)} {...props}>
      {children}
    </span>
  );
}

export interface ListItemTextProps {
  primary: ReactNode;
  secondary?: ReactNode;
  className?: string;
}

/** Primary and optional secondary text for a list item. */
export function ListItemText({ primary, secondary, className }: ListItemTextProps): JSX.Element {
  return (
    <span className={cx('nrx-list-item__text', className)}>
      <span className="nrx-list-item__primary">{primary}</span>
      {secondary != null ? <span className="nrx-list-item__secondary">{secondary}</span> : null}
    </span>
  );
}

export interface ListItemButtonProps {
  children: ReactNode;
  active?: boolean;
  indent?: 0 | 1 | 2;
  href?: string;
  onClick?: () => void;
  className?: string;
  'aria-current'?: 'page' | 'step' | 'location' | 'date' | 'time' | boolean;
}

/** Interactive list row — anchor when href is set, button otherwise. */
export function ListItemButton({
  children,
  active = false,
  indent = 0,
  href,
  onClick,
  className,
  'aria-current': ariaCurrent,
}: ListItemButtonProps): JSX.Element {
  const classes = cx(
    'nrx-list-item-button',
    active && 'nrx-list-item-button--active',
    indent === 1 && 'nrx-list-item-button--indent-1',
    indent === 2 && 'nrx-list-item-button--indent-2',
    className,
  );

  if (href != null) {
    return (
      <ListItem>
        <a
          href={href}
          className={classes}
          aria-current={active ? (ariaCurrent ?? 'page') : undefined}
          onClick={onClick}
        >
          {children}
        </a>
      </ListItem>
    );
  }

  return (
    <ListItem>
      <button
        type="button"
        className={classes}
        aria-current={active ? (ariaCurrent ?? 'page') : undefined}
        onClick={onClick}
      >
        {children}
      </button>
    </ListItem>
  );
}

export interface ListSubheaderProps extends ComponentPropsWithoutRef<'li'> {
  children: ReactNode;
}

/** Section label within a list. */
export function ListSubheader({ children, className, ...props }: ListSubheaderProps): JSX.Element {
  return (
    <li className={cx('nrx-list-subheader', className)} role="presentation" {...props}>
      {children}
    </li>
  );
}

export interface ListSectionProps {
  label: ReactNode;
  icon?: ReactNode;
  /** Initial expanded state when uncontrolled. */
  defaultOpen?: boolean;
  /** Controlled expanded state. */
  open?: boolean;
  /** Called when the section is expanded or collapsed. */
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
  className?: string;
}

/** Expandable list section with nested items (sidebar groups). */
export function ListSection({
  label,
  icon,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  children,
  className,
}: ListSectionProps): JSX.Element {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;

  function setOpen(next: boolean): void {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  }

  return (
    <li className={cx('nrx-list-section', className)}>
      <button
        type="button"
        className={cx(
          'nrx-list-item-button',
          'nrx-list-section__trigger',
          open && 'nrx-list-section__trigger--open',
        )}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
        <span className="nrx-list-item__primary">{label}</span>
        <span className="nrx-list-section__chevron" aria-hidden="true" />
      </button>
      <div
        className={cx('nrx-list-section__collapse', open && 'nrx-list-section__collapse--open')}
        aria-hidden={!open}
        {...(!open ? { inert: true } : {})}
      >
        <div className="nrx-list-section__collapse-inner">
          <List className="nrx-list-section__children">{children}</List>
        </div>
      </div>
    </li>
  );
}
